/**
 * CreateWorkspaceDialog — Phase 7 extract from pages/Workspace.jsx.
 * Personal/Shared toggle + tenant-wide/specific-members + member picker.
 *
 * iter-121.22:
 *  • Member picker now hides users from OTHER tenants (SA's /users is
 *    unscoped — without this filter, picking a cross-tenant user causes
 *    the backend to reject the share with "not in this tenant").
 *  • Catch block uses `friendlyApiError()` so plan-limit denials (HTTP
 *    402 PlanLimitExceeded → { message: "Multi Workspace isn't ..." })
 *    show the actual reason instead of the generic "Create failed".
 */
import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import friendlyApiError from "@/lib/apiErrors";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, Users as UsersIcon, Globe2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { KindTile } from "@/components/workspace/AssetGrid";

export default function CreateWorkspaceDialog({ open, onOpenChange, users, currentUser, onCreated }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [kind, setKind] = useState("personal");
    const [shareMode, setShareMode] = useState("tenant");
    const [members, setMembers] = useState([]); // [{user_id, permission}]
    const [memberPicker, setMemberPicker] = useState("");

    useEffect(() => {
        if (!open) {
            setName(""); setDescription(""); setKind("personal");
            setShareMode("tenant"); setMembers([]); setMemberPicker("");
        }
    }, [open]);

    // iter-121.22 — only same-tenant users are eligible. SA's /users
    // endpoint is unscoped by default so without this filter the picker
    // would surface other-tenant users → backend would then reject the
    // share with "not in this tenant". Falls through gracefully when
    // currentUser is missing (e.g. token expiry mid-render).
    const addableUsers = useMemo(() => {
        const tid = currentUser?.tenant_id;
        return (users || []).filter((u) =>
            u.id !== currentUser?.id &&
            (!tid || u.tenant_id === tid) &&
            !members.some((m) => m.user_id === u.id),
        );
    }, [users, currentUser, members]);

    const create = async () => {
        if (!name.trim()) { toast.error("Name is required"); return; }
        try {
            let finalMembers = members;
            if (kind === "shared" && shareMode === "members" && memberPicker && !members.some((m) => m.user_id === memberPicker)) {
                finalMembers = [...members, { user_id: memberPicker, permission: "view" }];
                setMembers(finalMembers);
            }
            const payload = {
                name: name.trim(), description, kind,
                share_mode: kind === "personal" ? "private" : shareMode,
                shared_with: kind === "shared" && shareMode === "members" ? finalMembers : [],
            };
            const r = await api.post("/workspaces", payload);
            toast.success("Workspace created");
            onCreated(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(friendlyApiError(e, "Create failed"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create workspace</DialogTitle></DialogHeader>

                <div>
                    <label className="eyebrow mb-1.5 block">Name *</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                           className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                           data-testid="workspace-name-input" />
                </div>
                <div>
                    <label className="eyebrow mb-1.5 block">Description</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                              className="w-full px-3 py-2 bg-transparent border border-border rounded-sm text-sm"
                              data-testid="workspace-desc-input" />
                </div>

                <div>
                    <label className="eyebrow mb-2 block">Workspace type *</label>
                    <div className="grid grid-cols-2 gap-2">
                        <KindTile label="Personal" icon={Lock} active={kind === "personal"}
                            desc="Private to you" onClick={() => setKind("personal")}
                            testId="workspace-kind-personal" />
                        <KindTile label="Shared" icon={UsersIcon} active={kind === "shared"}
                            desc="Collaborate with others" onClick={() => setKind("shared")}
                            testId="workspace-kind-shared" />
                    </div>
                </div>

                {kind === "shared" && (
                    <>
                        <div>
                            <label className="eyebrow mb-2 block">Who can access</label>
                            <div className="grid grid-cols-2 gap-2">
                                <KindTile label="Entity-wide" icon={Globe2} active={shareMode === "tenant"}
                                    desc="Everyone in your organization" onClick={() => setShareMode("tenant")}
                                    testId="workspace-share-tenant" />
                                <KindTile label="Specific people" icon={UsersIcon} active={shareMode === "members"}
                                    desc="Only selected members" onClick={() => setShareMode("members")}
                                    testId="workspace-share-members" />
                            </div>
                        </div>

                        {shareMode === "members" && (
                            <div>
                                <label className="eyebrow mb-1.5 block">Members ({members.length})</label>
                                {members.length > 0 && (
                                    <div className="space-y-1.5 mb-2">
                                        {members.map((m) => {
                                            const u = users.find((x) => x.id === m.user_id);
                                            return (
                                                <div key={m.user_id} className="flex items-center gap-2 p-2 border border-border rounded-sm bg-secondary/40">
                                                    <div className="w-6 h-6 bg-foreground text-background font-mono text-[9px] font-semibold flex items-center justify-center rounded-sm shrink-0">
                                                        {(u?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium truncate">{u?.name}</div>
                                                        <div className="text-[10px] font-mono text-muted-foreground truncate">{u?.email}</div>
                                                    </div>
                                                    <Select value={m.permission} onValueChange={(v) => setMembers(members.map((x) => x.user_id === m.user_id ? { ...x, permission: v } : x))}>
                                                        <SelectTrigger className="h-7 w-[90px] rounded-sm text-xs" data-testid={`member-perm-${m.user_id}`}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="view">View</SelectItem>
                                                            <SelectItem value="edit">Edit</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <button onClick={() => setMembers(members.filter((x) => x.user_id !== m.user_id))}
                                                            className="text-muted-foreground hover:text-primary p-1"
                                                            data-testid={`member-remove-${m.user_id}`}>
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {addableUsers.length > 0 && (
                                    <div className="flex gap-2">
                                        <Select value={memberPicker} onValueChange={setMemberPicker}>
                                            <SelectTrigger className="h-9 rounded-sm flex-1 text-sm" data-testid="workspace-member-picker"><SelectValue placeholder="Add a teammate..." /></SelectTrigger>
                                            <SelectContent>
                                                {addableUsers.map((u) => (
                                                    <SelectItem key={u.id} value={u.id}>{u.name} · <span className="font-mono text-xs text-muted-foreground">{u.email}</span></SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button variant="outline" className="rounded-sm h-9" disabled={!memberPicker}
                                                onClick={() => { setMembers([...members, { user_id: memberPicker, permission: "view" }]); setMemberPicker(""); }}
                                                data-testid="workspace-member-add">
                                            <Plus className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <DialogFooter>
                    <Button onClick={create} className="rounded-sm" data-testid="workspace-create-submit">Create workspace</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
